import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { useToast } from '@/components/ui/toast/use-toast'
import { logError } from '@/composables/useLogger'
import type {
  CollectionResponse,
  CollectionCreate,
  CollectionUpdate,
  CollectionBubbleAdd,
} from '~/types/collections'
import { useRuntimeConfig } from '#app'
import { useLogger } from '~/composables/useLogger'
import type { Collection, Bubble } from '~/stores/interfaces'
import { useBubblesStore } from '~/stores/bubbles'

export const useCollectionsStore = defineStore('collections', () => {
  const collections = ref<Record<string, Collection>>({})
  const loading = ref(false)
  const error = ref<string | null>(null)

  const { toast } = useToast()
  const runtimeConfig = useRuntimeConfig()
  const apiBase = runtimeConfig.public.apiBase

  // --- Internal Helper --- //
  const findCollectionIndex = (uuid: string): number =>
    Object.keys(collections.value).findIndex((key) => collections.value[key].uuid === uuid)

  // --- Actions --- //

  async function fetchCollections() {
    loading.value = true
    error.value = null
    try {
      const response = await $fetch<CollectionResponse[]>(`${apiBase}/collections`, {
        credentials: 'include',
        method: 'GET',
      })
      response.forEach((collection) => {
        collections.value[collection.uuid] = collection
      })
      return Object.values(collections.value)
    } catch (err) {
      logError('Failed to fetch collections:', err)
      error.value = 'Failed to load collections'
      toast({
        title: 'Error',
        description: error.value,
        variant: 'destructive',
      })
      return []
    } finally {
      loading.value = false
    }
  }

  async function getCollection(uuid: string): Promise<CollectionResponse | null> {
    // This action doesn't modify the main state but fetches details for one collection
    // Consider adding caching or checking local state first if needed
    try {
      const response = await $fetch<CollectionResponse>(`${apiBase}/collections/${uuid}`, {
        credentials: 'include',
        method: 'GET',
      })
      return response
    } catch (err) {
      logError(`Failed to fetch collection ${uuid}:`, err)
      toast({
        title: 'Error',
        description: 'Failed to load collection details',
        variant: 'destructive',
      })
      return null
    }
  }

  async function createCollection(data: CollectionCreate): Promise<CollectionResponse | null> {
    loading.value = true // Consider a specific loading state if needed
    error.value = null
    try {
      const newCollection = await $fetch<CollectionResponse>(`${apiBase}/collections`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      })
      collections.value[newCollection.uuid] = newCollection
      toast({
        title: 'Success',
        description: 'Collection created successfully',
      })
      return newCollection
    } catch (err) {
      logError('Failed to create collection:', err)
      error.value = 'Failed to create collection'
      toast({
        title: 'Error',
        description: error.value,
        variant: 'destructive',
      })
      return null
    } finally {
      loading.value = false
    }
  }

  async function updateCollection(
    uuid: string,
    data: CollectionUpdate
  ): Promise<CollectionResponse | null> {
    loading.value = true // Consider a specific loading state
    error.value = null
    try {
      const updatedCollection = await $fetch<CollectionResponse>(`${apiBase}/collections/${uuid}`, {
        method: 'PUT',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      })

      const index = findCollectionIndex(uuid)
      if (index !== -1) {
        collections.value[uuid] = { ...collections.value[uuid], ...updatedCollection }
      }

      toast({
        title: 'Success',
        description: 'Collection updated successfully',
      })
      return updatedCollection
    } catch (err) {
      logError(`Failed to update collection ${uuid}:`, err)
      error.value = 'Failed to update collection'
      toast({
        title: 'Error',
        description: error.value,
        variant: 'destructive',
      })
      return null
    } finally {
      loading.value = false
    }
  }

  async function deleteCollection(uuid: string): Promise<boolean> {
    loading.value = true // Consider specific loading state
    error.value = null
    try {
      await $fetch(`${apiBase}/collections/${uuid}`, {
        method: 'DELETE',
        credentials: 'include',
      })

      const index = findCollectionIndex(uuid)
      if (index !== -1) {
        const { [uuid]: _, ...rest } = collections.value
        collections.value = rest
      }

      toast({
        title: 'Success',
        description: 'Collection deleted successfully',
      })
      return true
    } catch (err) {
      logError(`Failed to delete collection ${uuid}:`, err)
      error.value = 'Failed to delete collection'
      toast({
        title: 'Error',
        description: error.value,
        variant: 'destructive',
      })
      return false
    } finally {
      loading.value = false
    }
  }

  async function addBubbleToCollection(
    collectionUuid: string,
    bubbleUuids: string[]
  ): Promise<CollectionResponse | null> {
    const body: CollectionBubbleAdd = { bubbleUuids }
    try {
      const updatedCollection = await $fetch<CollectionResponse>(
        `${apiBase}/collections/${collectionUuid}/bubbles`,
        {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(body),
        }
      )

      const index = findCollectionIndex(collectionUuid)
      if (index !== -1) {
        collections.value[collectionUuid] = updatedCollection
      }

      toast({
        title: 'Success',
        description: 'Bubbles added to collection',
      })
      return updatedCollection
    } catch (err) {
      logError(`Failed to add bubbles to collection ${collectionUuid}:`, err)
      toast({
        title: 'Error',
        description: 'Failed to add bubbles to collection',
        variant: 'destructive',
      })
      return null
    }
  }

  async function removeBubbleFromCollection(
    collectionUuid: string,
    bubbleUuid: string
  ): Promise<boolean> {
    try {
      await $fetch(`${apiBase}/collections/${collectionUuid}/bubbles/${bubbleUuid}`, {
        method: 'DELETE',
        credentials: 'include',
      })

      // Optimistically update the local state or refetch the collection
      const index = findCollectionIndex(collectionUuid)
      if (index !== -1) {
        const collection = collections.value[collectionUuid]
        if (collection && collection.bubbles) {
          const bubbleIndex = collection.bubbles.findIndex((b) => b.uuid === bubbleUuid)
          if (bubbleIndex !== -1) {
            collection.bubbles.splice(bubbleIndex, 1)
          }
        }
      } // Alternatively, call getCollection(collectionUuid) to refresh fully

      toast({
        title: 'Success',
        description: 'Bubble removed from collection',
      })
      return true
    } catch (err) {
      logError(`Failed to remove bubble ${bubbleUuid} from collection ${collectionUuid}:`, err)
      toast({
        title: 'Error',
        description: 'Failed to remove bubble from collection',
        variant: 'destructive',
      })
      return false
    }
  }

  async function addCollectionFromShare(shareUuid: string): Promise<boolean> {
    const logger = useLogger()
    const runtimeConfig = useRuntimeConfig()
    const bubblesStore = useBubblesStore()
    loading.value = true
    error.value = null

    try {
      logger.log(`Ingesting collection from share UUID: ${shareUuid}`)
      const newBubbles = await $fetch<Bubble[]>(
        `${runtimeConfig.public.apiBase}/collections/ingest/${shareUuid}`,
        {
          method: 'POST',
        }
      )

      // Add the new bubbles to the bubbles store
      if (newBubbles && newBubbles.length > 0) {
        newBubbles.forEach((bubble) => {
          bubblesStore.addBubble(bubble)
        })
      }
      logger.log(`Successfully ingested ${newBubbles.length} bubbles from share.`)

      return true
    } catch (err) {
      logError('Error ingesting collection from share:', err)
      error.value = 'Could not add the collection to your account. Please try again.'
      return false
    } finally {
      loading.value = false
    }
  }

  // --- Getters --- //
  const collectionList = computed(() => Object.values(collections.value))

  return {
    collections,
    loading,
    error,
    collectionList,
    fetchCollections,
    getCollection,
    createCollection,
    updateCollection,
    deleteCollection,
    addBubbleToCollection,
    removeBubbleFromCollection,
    addCollectionFromShare,
  }
})
